import { useCallback, useMemo, useReducer, useRef } from 'react'
import '../../../assets/css/style.css'
import '../../../assets/css/common.css'
import { DataBridgePageState, DataBridgeProps } from './extra/types'
import { produce } from 'immer'
import _set from 'lodash/set'
import _get from 'lodash/get'
import { DataBridgeImport } from './components/DataBridgeImport'
import { DataBridgeExport } from './components/DataBridgeExport'
import { downloadCsv } from './extra/helpers'
import { ChrofySelect } from '../../data/DataMapper/extra/common'

/**
 * @function dataBridgeReducer DataBridgeの状態管理
 * @param {import('./extra/types')._DataBridgePageState} state
 * @param {{ type: string; payload: any }} action
 * @returns
 */
const dataBridgeReducer = (state, action) => {
  const { type, payload } = action
  return produce(state, (draft) => {
    switch (type) {
      case 'UPDATE_STATE': {
        Object.assign(draft, payload)
        break
      }
      case 'UPDATE_PATH': {
        const { path, value } = payload
        _set(draft, path, value)
        break
      }
      default:
        break
    }
  })
}

/** @type {React.FC<import('zod').infer<DataBridgeProps>>} */
export const DataBridge = (_props) => {
  const {
    getRecords,
    onImport,
    onImportError,
    disabled = false,
    disabledImport,
    disabledExport,
    filename = 'Sample'
  } = DataBridgeProps.parse(_props)
  const importDisabled = disabledImport ?? disabled
  const exportDisabled = disabledExport ?? disabled

  const [s, _dispatch] = useReducer(
    dataBridgeReducer,
    {
      fileMeta: { name: '', size: 0, type: '' },
      selectedSheetIndex: null,
      sheetList: [],
      errors: []
    },
    DataBridgePageState.parse
  )
  const dispatch = useCallback((type, payload) => _dispatch({ type, payload }), [])

  const fileContentsRef = useRef([])

  const importedRecords = (selectedIndex) => _get(fileContentsRef.current, [selectedIndex, 'records'], [])
  const sheetOptions = s.sheetList.map((e, index) => ({ label: e, value: index }))

  /**
   * @param {object} fileMeta
   * @param {{ sheetName: string; records: Record<string, unknown>[] }[]} fileContents
   * @param {string[]} sheetList
   */
  const handleImport = async (fileMeta, fileContents, sheetList) => {
    clearErrors()
    onImportError?.('')

    fileContentsRef.current = fileContents

    const selectedSheetIndex = sheetList.findIndex((e) => e === fileContents[0].sheetName)

    const success = await onImport(importedRecords(selectedSheetIndex), fileMeta)

    if (!success) {
      return
    }

    dispatch('UPDATE_STATE', {
      fileMeta,
      selectedSheetIndex,
      sheetList
    })
  }

  /** @function clearErrors 初期化 */
  const clearErrors = () => {
    dispatch('UPDATE_PATH', 'errors', [])
  }

  /**
   * @function handleExport
   * @param {string} csvContent CSV形式の文字列
   * @param {string} exportFilename エクスポートファイル名（拡張子付き）
   */
  const handleExport = (csvContent, exportFilename) => {
    clearErrors()
    const csvFilename = exportFilename || `${filename}.csv`
    downloadCsv(csvFilename, csvContent)
  }

  const handleErrors = (error) => {
    dispatch('UPDATE_PATH', 'errors', [...s.errors, error])
    onImportError?.(error)
  }

  /**
   * @function handleChangeSheets シート変更の処理
   * @param {React.ChangeEvent} e
   */
  const handleChangeSheets = ({ target: { value } }) => {
    dispatch('UPDATE_STATE', {
      selectedSheetIndex: value
    })

    onImport(importedRecords(value), s.fileMeta)
  }

  return (
    <>
      <div className="content-footer !m-0 inline-flex min-w-0 max-w-full items-center gap-x-2">
        <p className="min-w-0 max-w-md truncate text-sm"> {s.fileMeta.name || ''}</p>
        {s.fileMeta.type === 'excel' && (
          <ChrofySelect
            data-testid="sheetSelect"
            className="w-min shrink-0"
            hasNoSelect={false}
            value={s.selectedSheetIndex || 0}
            options={sheetOptions}
            onChange={handleChangeSheets}
          />
        )}
        <div className="footer_button_area !w-fit shrink-0 !justify-center !gap-x-2">
          <DataBridgeImport disabled={importDisabled} onImport={handleImport} onError={handleErrors} />
          <DataBridgeExport
            disabled={exportDisabled}
            filename={filename}
            getRecords={getRecords}
            onExport={handleExport}
            onError={handleErrors}
          />
        </div>
      </div>

      {!!s.errors.length && (
        <ul className={`red--text`}>
          {s.errors.map((e, index) => (
            <li key={index}>{e}</li>
          ))}
        </ul>
      )}
    </>
  )
}
